import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Calendar, DollarSign, TrendingUp, Copy, Trash2, Download, XCircle } from 'lucide-react';
import { authUtils } from '../utils/auth';

const API_URL = '/api';

function Tracking() {
  const [deposits, setDeposits] = useState([]);
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    sortBy: 'tanggal',
    order: 'DESC'
  });
  const [depositModal, setDepositModal] = useState({ show: false, id: null, nominal: '' });
  // Checkbox states
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

useEffect(() => {
  fetchDeposits();
  fetchSummary();
}, [page, filters]);

useEffect(() => {
  // Reset selections when data changes
  setSelectedIds([]);
  setSelectAll(false);
}, [deposits]);

  const fetchDeposits = async () => {
    try {
      setLoading(true);
      const params = {
        page,
        limit: 50,
        sortBy: filters.sortBy,
        order: filters.order
      };

      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;

      const response = await axios.get(`${API_URL}/tracking`, { params });
      setDeposits(response.data.data);
      setTotalPages(response.data.totalPages);
    } catch (error) {
      console.error('Error fetching deposits:', error);
      alert('Gagal memuat data tracking');
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const params = {};
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;

      const response = await axios.get(`${API_URL}/tracking/summary`, { params });
      setSummary(response.data);
    } catch (error) {
      console.error('Error fetching summary:', error);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR'
    }).format(value);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('id-ID', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
	if (!dateString) return '-';
  const dt = new Date(dateString);
	if (Number.isNaN(dt.getTime())) return '-'; // ← TAMBAH INI (guard untuk invalid date)
	return dt.toLocaleString('id-ID', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
	});
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const handleDeposit = async () => {
    if (!depositModal.nominal || depositModal.nominal <= 0) {
      alert('Masukkan nominal deposit yang valid');
      return;
    }

    try {
      setLoading(true);
      await axios.post(`${API_URL}/tracking/deposit/${depositModal.id}`, {
        nominal_deposit: parseFloat(depositModal.nominal)
      });
      alert('✅ Deposit berhasil dicatat!');
      setDepositModal({ show: false, id: null, nominal: '' });
      fetchDeposits();
      fetchSummary(); // Refresh summary juga
    } catch (error) {
      console.error('Deposit error:', error);
      alert('❌ Gagal mencatat deposit: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };
  
  // Checkbox handlers
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedIds([]);
    } else {
      setSelectedIds(deposits.map(item => item.id));
    }
    setSelectAll(!selectAll);
  };

  const handleSelectOne = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(itemId => itemId !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  const handleCopySelected = () => {
    if (selectedIds.length === 0) {
      alert('Pilih data yang ingin dicopy terlebih dahulu');
      return;
    }

    const selectedData = deposits.filter(item => selectedIds.includes(item.id));
    
    // Format: Tanggal Blast | ID | Nomor | Nama Bank | Nomor Rek | Nama | Blast By | Brand | Nominal
    const copyText = selectedData.map(item =>
      `${formatDateTime(item.blast_date)}\t${item.user_id || '-'}\t${item.nomor}\t${item.nama_bank}\t${item.nomor_rekening}\t${item.nama || '-'}\t${item.blast_by_username || '-'}\t${item.blast_by_brand || '-'}\t${item.nominal_deposit || 0}`
    ).join('\n');

    const header = 'Tanggal Blast\tID\tNomor\tNama Bank\tNomor Rek\tNama\tBlast By\tBrand\tNominal Deposit\n';
    const fullText = header + copyText;

    navigator.clipboard.writeText(fullText).then(() => {
      alert(`✅ Berhasil copy ${selectedIds.length} data!\n\nData sudah ada di clipboard, paste di Excel/Spreadsheet.`);
    }).catch(err => {
      console.error('Copy failed:', err);
      alert('❌ Gagal copy data');
    });
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) {
      alert('Pilih data yang ingin dihapus terlebih dahulu');
      return;
    }

    if (!window.confirm(`⚠️ PERHATIAN!\n\nYakin ingin menghapus ${selectedIds.length} data tracking terpilih?\nTindakan ini tidak dapat dibatalkan!`)) {
      return;
    }

    try {
      setLoading(true);
      let successCount = 0;
      let failCount = 0;

      for (const id of selectedIds) {
        try {
          await axios.delete(`${API_URL}/tracking/${id}`);
          successCount++;
        } catch (error) {
          console.error(`Failed to delete ${id}:`, error);
          failCount++;
        }
      }

      alert(`✅ Selesai!\n\n${successCount} data berhasil dihapus${failCount > 0 ? `\n${failCount} data gagal dihapus` : ''}`);
      setSelectedIds([]);
      setSelectAll(false);
      fetchDeposits();
      fetchSummary();
    } catch (error) {
      console.error('Bulk delete error:', error);
      alert('❌ Gagal menghapus data');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkExport = async () => {
    if (selectedIds.length === 0) {
      alert('Pilih data yang ingin diexport terlebih dahulu');
      return;
    }

    try {
      setLoading(true);
      const selectedData = deposits.filter(item => selectedIds.includes(item.id));

      // Prepare data for Excel
      const exportData = selectedData.map(item => ({
        'Tanggal Blast': formatDateTime(item.blast_date),
        'ID': item.user_id || '-',
        'Nomor': item.nomor,
        'Nama Bank': item.nama_bank,
        'Nomor Rekening': item.nomor_rekening,
        'Nama': item.nama || '-',
        'Blast By': item.blast_by_username || '-',
        'Brand': item.blast_by_brand || '-',
        'Nominal Deposit': item.nominal_deposit || 0
      }));

      // Create worksheet
      const XLSX = require('xlsx');
      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Tracking Data');

      // Download
      XLSX.writeFile(workbook, `tracking_export_${new Date().toISOString().split('T')[0]}.xlsx`);

      alert(`✅ Berhasil export ${selectedIds.length} data!`);
    } catch (error) {
      console.error('Export error:', error);
      alert('❌ Gagal export data. Install xlsx library: npm install xlsx');
    } finally {
      setLoading(false);
    }
  };

  const handleClearSelection = () => {
    setSelectedIds([]);
    setSelectAll(false);
  };
  
  const totalDeposits = summary.reduce((sum, item) => sum + parseInt(item.jumlah_deposit || 0), 0);
  const totalNominal = summary.reduce((sum, item) => sum + parseFloat(item.total_nominal || 0), 0);

  return (
    <div className="tracking-page">
      <div className="page-header">
        <h1>Tracking Deposit</h1>
      </div>

      <div className="summary-cards">
        <div className="stat-card">
          <div className="stat-icon blue">
            <DollarSign size={32} />
          </div>
          <div className="stat-info">
            <p className="stat-label">Total Deposit</p>
            <p className="stat-value">{totalDeposits}</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon green">
            <TrendingUp size={32} />
          </div>
          <div className="stat-info">
            <p className="stat-label">Total Nominal</p>
            <p className="stat-value">{formatCurrency(totalNominal)}</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon purple">
            <Calendar size={32} />
          </div>
          <div className="stat-info">
            <p className="stat-label">Periode</p>
            <p className="stat-value">{summary.length} Hari</p>
          </div>
        </div>
      </div>

      <div className="filters">
        <div className="filter-group">
          <label>Tanggal Mulai</label>
          <input
            type="date"
            value={filters.startDate}
            onChange={(e) => handleFilterChange('startDate', e.target.value)}
            className="input"
          />
        </div>

        <div className="filter-group">
          <label>Tanggal Akhir</label>
          <input
            type="date"
            value={filters.endDate}
            onChange={(e) => handleFilterChange('endDate', e.target.value)}
            className="input"
          />
        </div>

        <div className="filter-group">
          <label>Sort By</label>
          <select
		  value={filters.sortBy}
		  onChange={(e) => handleFilterChange('sortBy', e.target.value)}
		  className="select"
		>
		  <option value="blast_date">Tanggal Blast</option>
		  <option value="deposit_tanggal">Tanggal Deposit</option>
		  <option value="nominal_deposit">Nominal Deposit</option>
		  <option value="created_at">Waktu Input</option>
		</select>
        </div>

        <div className="filter-group">
          <label>Order</label>
          <select
            value={filters.order}
            onChange={(e) => handleFilterChange('order', e.target.value)}
            className="select"
          >
            <option value="DESC">Terbaru</option>
            <option value="ASC">Terlama</option>
          </select>
        </div>
      </div>
	  
	  <div style={{ marginTop: '20px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
        {selectedIds.length > 0 && (
          <>
            <button onClick={handleCopySelected} className="btn btn-primary">
              <Copy size={18} />
              Copy ({selectedIds.length})
            </button>
            <button onClick={handleBulkDelete} className="btn btn-danger" disabled={loading}>
              <Trash2 size={18} />
              Delete ({selectedIds.length})
            </button>
            <button onClick={handleBulkExport} className="btn btn-success" disabled={loading}>
              <Download size={18} />
              Export ({selectedIds.length})
            </button>
            <button onClick={handleClearSelection} className="btn btn-secondary">
              <XCircle size={18} />
              Clear
            </button>
          </>
        )}
      </div>

      {summary.length > 0 && (
        <div className="summary-table-container">
          <h2>Ringkasan per Tanggal</h2>
          <table className="data-table">
            <thead>
              <tr>
                <th>Tanggal</th>
                <th>Jumlah Deposit</th>
                <th>Total Nominal</th>
                <th>Jumlah Batch</th>
              </tr>
            </thead>
            <tbody>
              {summary.map((item, index) => (
                <tr key={index}>
                  <td>{formatDate(item.tanggal)}</td>
                  <td>{item.jumlah_deposit}</td>
                  <td>{formatCurrency(item.total_nominal)}</td>
                  <td>{item.jumlah_batch}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {loading && <div className="loading">Loading...</div>}

      <div className="table-container">
        <h2>Detail Blast & Deposit</h2>
        <table className="data-table">
          <thead>
		  <tr>
			<th style={{ width: '40px' }}>
			  <input
				type="checkbox"
				checked={selectAll}
				onChange={handleSelectAll}
				style={{ cursor: 'pointer', width: '18px', height: '18px' }}
			  />
			</th>
			<th>Tanggal Blast</th>
			<th>ID</th>
			<th>Nomor</th>
			<th>Nama Bank</th>
			<th>Nomor Rek</th>
			<th>Nama</th>
			<th>Blast By</th>
			<th>Brand</th>
			<th>Nominal Deposit</th>
			<th>Actions</th>
		  </tr>
		</thead>
          <tbody>
		  {deposits.map((item) => (
			<tr key={item.id}>
			  <td>
				<input
				  type="checkbox"
				  checked={selectedIds.includes(item.id)}
				  onChange={() => handleSelectOne(item.id)}
				  style={{ cursor: 'pointer', width: '18px', height: '18px' }}
				/>
			  </td>
			  <td>{formatDateTime(item.blast_date)}</td>
			  <td>{item.user_id || '-'}</td>
			  <td>{item.nomor || '-'}</td>
			  <td>{item.nama_bank || '-'}</td>
			  <td>{item.nomor_rekening || '-'}</td>
			  <td>{item.nama || '-'}</td>
			  <td>{item.blast_by_username || '-'}</td>
			  <td>{item.blast_by_brand || '-'}</td>
			  <td className="text-right">
				{item.nominal_deposit > 0 ? formatCurrency(item.nominal_deposit) : 'Rp 0,00'}
			  </td>
			  <td>
			  {authUtils.canAccessTracking() && (
				<button
				  onClick={() => setDepositModal({ 
					show: true, 
					id: item.id, 
					nominal: item.nominal_deposit || '' 
				  })}
				  className="btn-icon btn-success"
				  title="Input/Update Deposit"
				>
				  <DollarSign size={16} />
				</button>
			  )}
			</td>
			</tr>
		  ))}
		</tbody>
        </table>
      </div>

      <div className="pagination">
        <button
          onClick={() => setPage(p => Math.max(1, p - 1))}
          disabled={page === 1}
          className="btn btn-secondary"
        >
          Previous
        </button>
        <span>Halaman {page} dari {totalPages}</span>
        <button
          onClick={() => setPage(p => Math.min(totalPages, p + 1))}
          disabled={page === totalPages}
          className="btn btn-secondary"
        >
          Next
        </button>
      </div>

      {depositModal.show && (
        <div className="modal">
          <div className="modal-content">
            <h2>Input Nominal Deposit</h2>
            <p className="text-muted">
              Nomor: {deposits.find(d => d.id === depositModal.id)?.nomor}
            </p>
            <input
              type="number"
              value={depositModal.nominal}
              onChange={(e) => setDepositModal({...depositModal, nominal: e.target.value})}
              placeholder="Masukkan nominal"
              className="input"
            />
            <div className="modal-buttons">
              <button onClick={handleDeposit} className="btn btn-primary">
                Simpan
              </button>
              <button 
                onClick={() => setDepositModal({ show: false, id: null, nominal: '' })} 
                className="btn btn-secondary"
              >
                Batal
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Tracking;